/**
 * Centralized Branding Configuration
 */

export interface BrandingConfig {
  company: { name: string; tagline?: string; copyright?: string; };
  logos: { primary: string; wordmark: string; favicon?: string; };
  colors: {
    primary: string; secondary: string; tertiary: string; accent: string; ring: string;
    gradient: { start: string; end: string; angle: number; };
    chart: string[];
    device?: { ios: string; android: string; };
    gender?: { male: string; female: string; };
    bar?: string;
    background?: { light: string; dark: string; };
    card?: { light: string; dark: string; };
  };
  email: { headerGradientStart: string; headerGradientEnd: string; buttonBackground: string; accentText: string; };
}

export const branding: BrandingConfig = {
  company: {
    name: "Drive Fitness",
    tagline: "Sales Dashboard",
    copyright: "Drive Fitness",
  },
  logos: { primary: "/logo-v2.png", wordmark: "/logo-v2.png", favicon: "/favicon.ico" },
  colors: {
    primary:   "#4B2FA0",
    secondary: "#7B5CE4",
    tertiary:  "#2D1B7A",
    accent:    "#C8B8F0",
    ring:      "#4B2FA0",
    gradient:  { start: "#7B5CE4", end: "#2D1B7A", angle: 135 },
    chart: [
      "#4B2FA0", "#7B5CE4", "#2D1B7A", "#6D28D9",
      "#5B21B6", "#A78BFA", "#C8B8F0", "#DDD6FE",
    ],
    device:  { ios: "#4B2FA0", android: "#7B5CE4" },
    gender:  { male: "#4B2FA0", female: "#A78BFA" },
    bar: "#111827",
    background: { light: "#f5f5f5", dark: "#0a0812" },
    card:       { light: "#ffffff",  dark: "#150f2a" },
  },
  email: {
    headerGradientStart: "#7B5CE4",
    headerGradientEnd:   "#2D1B7A",
    buttonBackground:    "#4B2FA0",
    accentText:          "#4B2FA0",
  },
};
export function getGradientCSS(gradient = branding.colors.gradient): string {
  return `linear-gradient(${gradient.angle}deg, ${gradient.start} 0%, ${gradient.end} 100%)`;
}
export function getGradientTailwind(gradient = branding.colors.gradient): string {
  return `from-[${gradient.start}] to-[${gradient.end}]`;
}
export function getChartColor(index: number): string {
  const chart = branding.colors.chart;
  return chart[index % chart.length] ?? branding.colors.primary;
}
export function injectBrandingCSS(): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const { colors } = branding;
  root.style.setProperty("--brand-primary", colors.primary);
  root.style.setProperty("--brand-secondary", colors.secondary);
  root.style.setProperty("--brand-tertiary", colors.tertiary);
  root.style.setProperty("--brand-accent", colors.accent);
  root.style.setProperty("--brand-ring", colors.ring);
  root.style.setProperty("--brand-gradient-start", colors.gradient.start);
  root.style.setProperty("--brand-gradient-end", colors.gradient.end);
  const bgLight = colors.background?.light ?? "#f5f5f5";
  const bgDark  = colors.background?.dark  ?? "#0a0a0a";
  const cardLight = colors.card?.light ?? "#ffffff";
  const cardDark  = colors.card?.dark  ?? "#171717";
  root.style.setProperty("--brand-bg-light",   bgLight);
  root.style.setProperty("--brand-bg-dark",    bgDark);
  root.style.setProperty("--brand-card-light", cardLight);
  root.style.setProperty("--brand-card-dark",  cardDark);
  root.style.setProperty("--primary", colors.primary);
  root.style.setProperty("--ring",    colors.ring);
  const isDark    = root.classList.contains("dark");
  const isCrimson = root.classList.contains("crimson");
  if (isCrimson) {
    root.style.setProperty("--background", bgDark);
    root.style.setProperty("--card", "rgba(255,255,255,0.93)");
    root.style.setProperty("--foreground", "#ffffff");
    root.style.setProperty("--card-foreground", "#1a0404");
    root.style.setProperty("--popover", "rgba(255,255,255,0.97)");
    root.style.setProperty("--popover-foreground", "#1a0404");
    root.style.setProperty("--border", "rgba(255,255,255,0.28)");
    root.style.setProperty("--muted-foreground", "rgba(255,255,255,0.75)");
  } else if (isDark) {
    root.style.setProperty("--background", bgDark);
    root.style.setProperty("--card", cardDark);
    colors.chart.slice(0,5).forEach((c,i) => {
      root.style.setProperty(`--chart-${i+1}`, c);
    });
  } else {
    colors.chart.forEach((c,i) => root.style.setProperty(`--chart-${i+1}`, c));
    root.style.setProperty("--background", bgLight);
    root.style.setProperty("--card", cardLight);
    root.style.removeProperty("--foreground");
    root.style.removeProperty("--card-foreground");
    root.style.removeProperty("--popover");
    root.style.removeProperty("--popover-foreground");
    root.style.removeProperty("--border");
    root.style.removeProperty("--muted-foreground");
  }
}
export function getCollectionPrefix(): string {
  return branding.company.name.toLowerCase().replace(/[^a-z0-9]+/g,"_").replace(/^_+|_+$/g,"") + "_";
}
export function getCollectionName(baseName: string): string { return getCollectionPrefix() + baseName; }
export function getModelName(baseName: string): string {
  return branding.company.name.replace(/[^a-zA-Z0-9]+/g,"") + "_" + baseName;
}
